'use client';

import { useAuthState } from '@/app/_providers/store';
import BarLoader from '@/components/ui/loaders/BarLoader';
import {
   Box,
   Button,
   Checkbox,
   createListCollection,
   Dialog,
   Field,
   Input,
   InputGroup,
   Portal,
   Select,
   Stack,
   Switch,
   Textarea,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ClinicsPackageSelectionSchema, TClinicsPackageSelection } from './clinicsPackageSelection.schema';

type Props = {
   isOpen: boolean;
   onClose: () => void;
   setPackages: Dispatch<SetStateAction<TClinicsPackageSelection[]>>;
};
const ClinicsPackageSelectionForm = ({ isOpen, onClose, setPackages }: Props) => {
   const {
      register,
      handleSubmit,
      formState: { errors },
      setValue,
      setError,
      watch,
   } = useForm<TClinicsPackageSelection>({
      resolver: zodResolver(ClinicsPackageSelectionSchema),
      defaultValues: {
         package_name: '',
         package_price: 0,
         priceOption: 'month',
         consultation_decided: false,
         isFreeConsultation: false,
         consultation_fee_from: 0,
         consultation_fee_to: 0,
         description: '',
      },
   });
   const { user } = useAuthState();

   const { consultation_decided } = watch();

   const PackagePriceSelectOptions = createListCollection({
      items: [
         { label: 'Monthly', value: 'month' },
         { label: 'Weekly', value: 'week' },
      ],
   });

   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [isChecked, setIsChecked] = useState<boolean>(false);

   const onSubmit = handleSubmit(async data => {
      if (watch('consultation_fee_from') > watch('consultation_fee_to')) {
         setError('consultation_fee_to', {
            message: 'Consultation fee to must be greater than consultation fee from',
         });
         return;
      }
      setIsLoading(true);
      setPackages(prev => [...prev, data]);
      setIsLoading(false);
      onClose();
   });

   if (!user) {
      return;
   }

   const handleCheck = (checked: boolean | 'indeterminate') => {
      if (checked === 'indeterminate') return;
      setIsChecked(checked);
      setValue('isFreeConsultation', checked);
   };

   return (
      <>
         {isLoading && <BarLoader loadingText="Verifying" />}
         <Dialog.Root placement="center" motionPreset="slide-in-bottom" open={isOpen} size="sm">
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner
                  style={{
                     height: 'auto',
                     top: '30px',
                     bottom: '30px',
                  }}
               >
                  <Dialog.Content bg="#202020" rounded="15px" py="4" px="5" maxWidth="500px">
                     <Dialog.Header>
                        <Dialog.Title color="white" textStyle="4xl" textAlign="center" mb="2" width="100%">
                           Package Request
                        </Dialog.Title>
                     </Dialog.Header>
                     <Dialog.Body>
                        <form onSubmit={onSubmit}>
                           <Stack gap="4" align="flex-start" maxW="sm">
                              <Field.Root invalid={!!errors.package_name}>
                                 <Field.Label textStyle="xs" color="white" mb="1">
                                    Enter package name:
                                 </Field.Label>
                                 <Input
                                    placeholder="Enter package name"
                                    variant="subtle"
                                    h="10"
                                    rounded="12px"
                                    bg="#414141"
                                    color="#ffffff"
                                    _placeholder={{ color: '#7A7A7A' }}
                                    {...register('package_name')}
                                 />
                                 {errors.package_name && (
                                    <Field.ErrorText>{errors.package_name.message}</Field.ErrorText>
                                 )}
                              </Field.Root>

                              <Checkbox.Root
                                 checked={isChecked}
                                 onCheckedChange={({ checked }) => handleCheck(checked)}
                              >
                                 <Checkbox.HiddenInput />
                                 <Checkbox.Control
                                    css={{
                                       '[data-state=checked] & svg': {
                                          stroke: '#222a31 !important',
                                       },
                                    }}
                                 />
                                 <Checkbox.Label textStyle="xs" color="white" mb="1">
                                    Consultation for free.
                                 </Checkbox.Label>
                                 {errors.isFreeConsultation && (
                                    <Field.ErrorText>{errors.isFreeConsultation.message}</Field.ErrorText>
                                 )}
                              </Checkbox.Root>
                              {!isChecked && (
                                 <>
                                    <Field.Root invalid={!!errors.consultation_fee_from}>
                                       <Field.Label textStyle="xs" color="white" mb="1">
                                          Consultation price from:
                                       </Field.Label>
                                       <Box position="relative" w="full">
                                          <InputGroup
                                             startElement={
                                                <Box color="#fff" fontSize="sm">
                                                   £
                                                </Box>
                                             }
                                             endElement={
                                                <Box color="#fff" fontSize="sm">
                                                   GBP
                                                </Box>
                                             }
                                          >
                                             <Input
                                                placeholder="Enter consultation fee from"
                                                variant="subtle"
                                                h="10"
                                                rounded="12px"
                                                bg="#414141"
                                                color="#ffffff"
                                                _placeholder={{ color: '#7A7A7A' }}
                                                {...register('consultation_fee_from', { valueAsNumber: true })}
                                             />
                                          </InputGroup>
                                          {errors.consultation_fee_from && (
                                             <Field.ErrorText>{errors.consultation_fee_from.message}</Field.ErrorText>
                                          )}
                                       </Box>
                                    </Field.Root>

                                    <Field.Root invalid={!!errors.consultation_fee_to}>
                                       <Field.Label textStyle="xs" color="white" mb="1">
                                          Consultation price to:
                                       </Field.Label>
                                       <Box position="relative" w="full">
                                          <InputGroup
                                             startElement={
                                                <Box color="#fff" fontSize="sm">
                                                   £
                                                </Box>
                                             }
                                             endElement={
                                                <Box color="#fff" fontSize="sm">
                                                   GBP
                                                </Box>
                                             }
                                          >
                                             <Input
                                                placeholder="Enter consultation fee to"
                                                variant="subtle"
                                                h="10"
                                                rounded="12px"
                                                bg="#414141"
                                                color="#ffffff"
                                                _placeholder={{ color: '#7A7A7A' }}
                                                {...register('consultation_fee_to', { valueAsNumber: true })}
                                             />
                                          </InputGroup>
                                          {errors.consultation_fee_to && (
                                             <Field.ErrorText>{errors.consultation_fee_to.message}</Field.ErrorText>
                                          )}
                                       </Box>
                                    </Field.Root>
                                 </>
                              )}

                              <Field.Root invalid={!!errors.package_price}>
                                 <Field.Label textStyle="xs" color="white" mb="1">
                                    Enter Price:
                                 </Field.Label>
                                 <InputGroup
                                    startElement={
                                       <Box color="#fff" fontSize="sm">
                                          £
                                       </Box>
                                    }
                                    endElement={
                                       <Box color="#fff" fontSize="sm">
                                          GBP
                                       </Box>
                                    }
                                 >
                                    <Input
                                       placeholder="Enter price"
                                       variant="subtle"
                                       h="10"
                                       rounded="12px"
                                       bg="#414141"
                                       color="#ffffff"
                                       _placeholder={{ color: '#7A7A7A' }}
                                       {...register('package_price')}
                                    />
                                 </InputGroup>
                                 {errors.package_price && (
                                    <Field.ErrorText>{errors.package_price.message}</Field.ErrorText>
                                 )}
                              </Field.Root>

                              <Field.Root>
                                 <Select.Root collection={PackagePriceSelectOptions} size="sm" width="100%">
                                    <Select.HiddenSelect />
                                    <Select.Label textStyle="xs" color="white" mb="1">
                                       Select a option (Monthly/Weekly):
                                    </Select.Label>
                                    <Select.Control>
                                       <Select.Trigger
                                          style={{
                                             border: 'none',
                                             backgroundColor: '#414141',
                                             color: '#ffffff',
                                             borderRadius: '12px',
                                             height: '40px',
                                          }}
                                       >
                                          <Select.ValueText
                                             placeholder="Select Option"
                                             css={{
                                                '&[data-placeholder]': {
                                                   color: '#fff!importnat',
                                                },
                                             }}
                                          />
                                       </Select.Trigger>
                                       <Select.IndicatorGroup>
                                          <Select.Indicator
                                             css={{
                                                color: '#fff',
                                             }}
                                          />
                                       </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                       <Select.Positioner>
                                          <Select.Content
                                             zIndex={90000}
                                             style={{
                                                backgroundColor: '#141414fc',
                                                color: '#fff',
                                             }}
                                          >
                                             {PackagePriceSelectOptions.items.map(option => (
                                                <Select.Item
                                                   item={option}
                                                   key={option.value}
                                                   {...register('priceOption')}
                                                >
                                                   {option.label}
                                                   <Select.ItemIndicator />
                                                   {errors.priceOption && (
                                                      <Field.ErrorText>{errors.priceOption.message}</Field.ErrorText>
                                                   )}
                                                </Select.Item>
                                             ))}
                                          </Select.Content>
                                       </Select.Positioner>
                                    </Portal>
                                 </Select.Root>
                              </Field.Root>

                              <Field.Root>
                                 <Field.Label textStyle="xs" color="white" mb="1">
                                    Description(Summarise your offer) :
                                 </Field.Label>
                                 <Textarea
                                    placeholder="summarise your offer"
                                    variant="subtle"
                                    h="100"
                                    rounded="12px"
                                    bg="#414141"
                                    color="#ffffff"
                                    _placeholder={{ color: '#7A7A7A' }}
                                    {...register('description')}
                                 />
                                 {errors.description && <Field.ErrorText>{errors.description.message}</Field.ErrorText>}
                              </Field.Root>

                              <Switch.Root
                                 name="consultation_decided"
                                 checked={consultation_decided}
                                 onCheckedChange={({ checked }) => {
                                    setValue('consultation_decided', checked);
                                 }}
                              >
                                 <Switch.HiddenInput />
                                 <Switch.Control
                                    css={{
                                       '[data-state=checked] &': {
                                          backgroundColor: '#f5e876', // ON color
                                       },
                                       '[data-state=unchecked] &': {
                                          backgroundColor: '#777', // OFF color
                                       },

                                       // Thumb
                                       '& > span': {
                                          backgroundColor: '#222a31',
                                       },
                                    }}
                                 />
                                 <Switch.Label textStyle="xs" color="white" mb="1">
                                    Allow customer to have consultation before choosing package.
                                 </Switch.Label>
                              </Switch.Root>

                              <Dialog.Footer justifyContent="center" pt="0">
                                 <Button
                                    variant="outline"
                                    h="10"
                                    w="auto"
                                    bg="darkerBtn"
                                    color="white"
                                    border="none"
                                    textStyle="xs"
                                    fontWeight="600"
                                    onClick={onClose}
                                    _hover={{ bg: '#e64343' }}
                                 >
                                    Cancel
                                 </Button>
                                 <Button
                                    type="submit"
                                    h="10"
                                    w="auto"
                                    textStyle="xs"
                                    fontWeight="600"
                                    _hover={{ color: '#fff' }}
                                 >
                                    Submit
                                 </Button>
                              </Dialog.Footer>
                           </Stack>
                        </form>
                     </Dialog.Body>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root>
      </>
   );
};

export default ClinicsPackageSelectionForm;
